import { useEffect, useRef, useState } from 'react';
import type { Audience, Organization } from '@epasu/core';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
  /** null = neue Firma */
  organization: Organization | null;
  audiences: Audience[];
  /**
   * Zielgruppe festnageln, statt sie zur Wahl zu stellen. Gesetzt, wenn der
   * Dialog aus einer Kontaktakte heraus aufgeht: die Firma muss dann zur
   * Zielgruppe des Kontakts passen, sonst lässt sich beides nicht verbinden.
   */
  lockedAudienceId?: string;
  onClose: () => void;
  /** Bekommt die angelegte bzw. geänderte Firma — der Aufrufer verknüpft weiter. */
  onSaved: (organization: Organization) => void;
}

/**
 * Firma anlegen und bearbeiten.
 *
 * Die Domain ist das wichtigste Feld: Über sie ordnet der Postfach-Abgleich
 * eingehende Nachrichten automatisch der Firma zu, auch wenn der Absender noch
 * kein Kontakt ist. Deshalb steht der Hinweis direkt am Feld.
 */
export function OrganizationDialog({
  organization,
  audiences,
  lockedAudienceId,
  onClose,
  onSaved,
}: Props) {
  const [audienceId, setAudienceId] = useState(
    organization?.audienceId ?? lockedAudienceId ?? audiences[0]?.id ?? '',
  );
  const [name, setName] = useState(organization?.name ?? '');
  const [domain, setDomain] = useState(organization?.domain ?? '');
  const [website, setWebsite] = useState(organization?.website ?? '');
  const [email, setEmail] = useState(organization?.email ?? '');
  const [phone, setPhone] = useState(organization?.phone ?? '');
  const [street, setStreet] = useState(organization?.street ?? '');
  const [postalCode, setPostalCode] = useState(organization?.postalCode ?? '');
  const [city, setCity] = useState(organization?.city ?? '');
  const [industry, setIndustry] = useState(organization?.industry ?? '');
  const [region, setRegion] = useState(organization?.region ?? '');
  const [notes, setNotes] = useState(organization?.notes ?? '');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  /**
   * Nur beim Bearbeiten von Belang: Beim Anlegen hängt noch nichts an der
   * Firma, das mitziehen oder stehenbleiben könnte.
   */
  const wechseltZielgruppe =
    organization !== null && audienceId !== organization.audienceId;

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Bitte einen Namen angeben.');
      return;
    }
    if (!audienceId) {
      setError('Bitte eine Zielgruppe wählen.');
      return;
    }

    const felder = {
      name,
      domain,
      website,
      email,
      phone,
      street,
      postalCode,
      city,
      industry,
      region,
      notes,
    };

    setBusy(true);
    setError(null);
    try {
      const gespeichert = organization
        ? await crm.organizations.update(organization.id, { audienceId, ...felder })
        : await crm.organizations.create({ audienceId, ...felder });
      if (!gespeichert) throw new Error('Die Firma wurde nicht gespeichert.');
      onSaved(gespeichert);
    } catch (err) {
      const text = err instanceof Error ? err.message : 'Speichern fehlgeschlagen';
      setError(text);
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={organization ? 'Firma bearbeiten' : 'Neue Firma'}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => void submit(e)}
        className="my-auto w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">
          {organization ? 'Firma bearbeiten' : 'Neue Firma'}
        </h2>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Name" className="sm:col-span-2">
            <Input
              ref={nameRef}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Musterschule Köln"
            />
          </Field>

          <Field label="Domain" className="sm:col-span-2">
            <Input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="musterschule.de"
            />
            <span className="text-xs text-muted-foreground">
              Über die Domain ordnet der Postfach-Abgleich eingehende Nachrichten
              dieser Firma zu, auch bei unbekannten Absendern. Freemail-Adressen
              wie gmx.de werden dabei nie verwendet.
            </span>
          </Field>

          <Field label="Website">
            <Input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://…"
            />
          </Field>
          <Field label="Telefon">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>

          <Field label="E-Mail" className="sm:col-span-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="info@musterschule.de"
            />
            <span className="text-xs text-muted-foreground">
              Die zentrale Adresse des Hauses. Die Adressen der Ansprechpartner
              stehen an den Kontakten — über die läuft die Zuordnung eingehender
              Post.
            </span>
          </Field>

          <Field label="Straße" className="sm:col-span-2">
            <Input value={street} onChange={(e) => setStreet(e.target.value)} />
          </Field>
          <Field label="PLZ">
            <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
          </Field>
          <Field label="Ort">
            <Input value={city} onChange={(e) => setCity(e.target.value)} />
          </Field>

          <Field label="Branche">
            <Input
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="Schul- und Kindergartenfotografie"
            />
          </Field>
          <Field label="Einsatzgebiet">
            <Input
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="Raum Leipzig, bundesweit"
            />
          </Field>

          <Field label="Notiz" className="sm:col-span-2">
            <textarea
              className="min-h-[5rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Warum die Firma passt, Besonderheiten …"
            />
          </Field>

          {!lockedAudienceId && (
            <Field label="Zielgruppe" className="sm:col-span-2">
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={audienceId}
                onChange={(e) => setAudienceId(e.target.value)}
              >
                {audiences.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
              {wechseltZielgruppe && (
                <span className="text-xs text-muted-foreground">
                  Die Ansprechpartner der Firma wechseln mit. Deals bleiben in
                  ihrer bisherigen Pipeline stehen — welche Stufe dort drüben
                  die richtige wäre, ließe sich nur raten.
                </span>
              )}
            </Field>
          )}

          {!organization && lockedAudienceId && (
            <p className="text-xs text-muted-foreground sm:col-span-2">
              Zielgruppe:{' '}
              <strong>
                {audiences.find((a) => a.id === lockedAudienceId)?.name ?? '—'}
              </strong>{' '}
              — übernommen vom Kontakt, damit sich beides verbinden lässt.
            </p>
          )}
        </div>

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Speichere …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
